/**
 * The "Fazly vs X" pages.
 *
 * ── Why these exist ──────────────────────────────────────────────────────────
 *
 * Comparison and "best X" formats are 43.8% of everything ChatGPT cites
 * (docs/SEO-GEO-RESEARCH.md, finding 1), and the Tally playbook that finding
 * comes wrapped in is a comparison hub: direct competitor pages structured the
 * way an AI answer is structured — rankings, "best for" labels, feature rows,
 * a direct recommendation. This is that hub.
 *
 * ── The shape is designed to make fabrication hard ───────────────────────────
 *
 * A comparison page is the easiest page on any site to lie on, and the lies are
 * the kind nobody notices for a year: a competitor's price that was true once, a
 * feature they shipped last month, a limitation they never had. The live v1
 * site's versions of these pages made specific claims about six other products'
 * pricing and features, and there is no process here that keeps those true.
 *
 * So the type does not have a field for "their price" or "their features". Each
 * row states a DIMENSION worth comparing on and what FAZLY does about it —
 * something this repo knows and this repo can keep true — and every page tells
 * the reader plainly to check the other product's current answer on its own
 * site. That is less impressive and it is the version we can stand behind.
 *
 * The `theirStrength` field exists for the same reason in the other direction:
 * a comparison page that finds nothing good to say about the alternative is an
 * advertisement, and both readers and assistants discount it accordingly. Each
 * page names something the other product is genuinely better at.
 */

/** The competitors with a page. Slugs match the v1 site's URLs exactly. */
export const COMPARE_SLUGS = [
  "umax",
  "qoves",
  "mirror",
  "looksmax-ai",
  "cal-ai",
  "aire",
] as const;

export type CompareSlug = (typeof COMPARE_SLUGS)[number];

export interface CompareRow {
  /** The dimension, phrased as what a reader is deciding between. */
  readonly dimension: string;
  /** What Fazly does. A fact about this repo, checkable from it. */
  readonly fazly: string;
}

export interface CompareCopy {
  /** The other product's display name, e.g. "UMAX". */
  readonly rival: string;
  readonly title: string;
  readonly description: string;
  readonly h1: string;
  readonly standfirst: string;
  /** 134–167 words, self-contained, answer first. */
  readonly answer: string;
  /** "Best for" — the reader Fazly suits. Explicitly not "best overall". */
  readonly bestForFazly: string;
  /** "Best for" — the reader the other product suits better. */
  readonly bestForRival: string;
  /** Something the other product is genuinely better at. */
  readonly theirStrength: string;
  readonly rows: readonly CompareRow[];
  /** The standing caveat, rendered on the page next to the table. */
  readonly caveat: string;
  readonly faq: readonly { q: string; a: string }[];
}
